import { useEffect, useRef, useState, useCallback } from "react";
import { signOut } from "firebase/auth";
import { doc, setDoc, onSnapshot, collection, addDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

const INACTIVITY_MS = 2 * 60 * 60 * 1000;    // 2 hours
const WARNING_MS = 60 * 1000;
const HARD_LIMIT_MS = 8 * 60 * 60 * 1000;
const LS_LOGIN = "ns-login-time";
const LS_ACTIVITY = "ns-last-activity";
const LS_SESSION = "ns-session-id";

function genId() {
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

async function logAudit(uid, email, action, details) {
  try {
    await addDoc(collection(db, "audit_log"), {
      uid, email: email || "", action, details: details || {},
      userAgent: navigator.userAgent || "",
      timestamp: serverTimestamp(),
    });
  } catch (e) { /* ok */ }
}

export default function useSessionSecurity(authUser) {
  const [showWarning, setShowWarning] = useState(false);
  const [warningSeconds, setWarningSeconds] = useState(60);
  const [logoutReason, setLogoutReason] = useState("");
  const sessionIdRef = useRef(null);
  const loggedOutRef = useRef(false);
  const countdownRef = useRef(null);
  const showWarningRef = useRef(false);

  const touch = () => { try { localStorage.setItem(LS_ACTIVITY, String(Date.now())); } catch(e){} };
  const getLastActivity = () => { try { return parseInt(localStorage.getItem(LS_ACTIVITY)) || Date.now(); } catch(e){ return Date.now(); } };
  const getLoginTime = () => { try { return parseInt(localStorage.getItem(LS_LOGIN)) || Date.now(); } catch(e){ return Date.now(); } };

  const doLogout = useCallback(async (reason) => {
    if (loggedOutRef.current) return;
    loggedOutRef.current = true;
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    setShowWarning(false);
    showWarningRef.current = false;
    setLogoutReason(reason);

    if (authUser) {
      await logAudit(authUser.uid, authUser.email, "logout", { reason, sessionId: sessionIdRef.current });
      try {
        const snap = await getDoc(doc(db, "sessions", authUser.uid));
        if (snap.exists() && snap.data().sessionId === sessionIdRef.current) {
          await setDoc(doc(db, "sessions", authUser.uid), { sessionId: null, loggedOutAt: serverTimestamp() }, { merge: true });
        }
      } catch (e) { /* ok */ }
    }

    try { localStorage.removeItem(LS_LOGIN); localStorage.removeItem(LS_SESSION); } catch(e){}
    try { await signOut(auth); } catch (e) { /* ok */ }
  }, [authUser]);

  // ── 1. Register session + single device ──
  useEffect(() => {
    if (!authUser?.uid) return;
    loggedOutRef.current = false;

    let sid = null;
    try { sid = localStorage.getItem(LS_SESSION); } catch(e){}

    if (!sid) {
      sid = genId();
      try {
        localStorage.setItem(LS_SESSION, sid);
        localStorage.setItem(LS_LOGIN, String(Date.now()));
        localStorage.setItem(LS_ACTIVITY, String(Date.now()));
      } catch(e){}

      setDoc(doc(db, "sessions", authUser.uid), {
        sessionId: sid, email: authUser.email || "",
        loggedInAt: serverTimestamp(), userAgent: navigator.userAgent || "",
      }).catch(() => {});
      logAudit(authUser.uid, authUser.email, "login", { sessionId: sid });
    }
    sessionIdRef.current = sid;

    const unsub = onSnapshot(doc(db, "sessions", authUser.uid), snap => {
      if (!snap.exists() || loggedOutRef.current) return;
      const data = snap.data();
      if (data.sessionId && data.sessionId !== sessionIdRef.current) {
        doLogout("Signed in from another device");
      }
    });
    return () => unsub();
  }, [authUser?.uid, doLogout]);

  // ── 2. Activity tracking + checks ──
  useEffect(() => {
    if (!authUser) return;

    const onActivity = () => {
      touch();
      if (showWarningRef.current) {
        setShowWarning(false);
        showWarningRef.current = false;
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
      }
    };

    const events = ["mousedown", "mousemove", "keydown", "keyup", "click", "touchstart", "scroll", "wheel", "pointerdown", "input", "change"];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    events.forEach(e => document.addEventListener(e, onActivity, { passive: true }));

    const checker = setInterval(() => {
      if (loggedOutRef.current) return;
      const now = Date.now();
      const idle = now - getLastActivity();
      const sessionAge = now - getLoginTime();

      if (sessionAge >= HARD_LIMIT_MS) { doLogout("Session expired (8 hour limit)"); return; }
      if (idle >= INACTIVITY_MS) { doLogout("Inactivity timeout"); return; }

      if (idle >= INACTIVITY_MS - WARNING_MS && !showWarningRef.current) {
        showWarningRef.current = true;
        setShowWarning(true);
        setWarningSeconds(Math.ceil((INACTIVITY_MS - idle) / 1000));
        if (!countdownRef.current) {
          countdownRef.current = setInterval(() => {
            const currentIdle = Date.now() - getLastActivity();
            if (currentIdle < INACTIVITY_MS - WARNING_MS) {
              setShowWarning(false);
              showWarningRef.current = false;
              clearInterval(countdownRef.current);
              countdownRef.current = null;
              return;
            }
            const secs = Math.ceil((INACTIVITY_MS - currentIdle) / 1000);
            if (secs <= 0) { doLogout("Inactivity timeout"); }
            else { setWarningSeconds(secs); }
          }, 1000);
        }
      }
    }, 5000);

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !loggedOutRef.current) {
        const now = Date.now();
        if (now - getLoginTime() >= HARD_LIMIT_MS) { doLogout("Session expired (8 hour limit)"); return; }
        if (now - getLastActivity() >= INACTIVITY_MS) { doLogout("Inactivity timeout"); return; }
        touch();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(checker);
      events.forEach(e => window.removeEventListener(e, onActivity));
      events.forEach(e => document.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    };
  }, [authUser, doLogout]);

  const dismissWarning = useCallback(() => {
    touch();
    setShowWarning(false);
    showWarningRef.current = false;
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  return { showWarning, warningSeconds, dismissWarning, logoutReason };
}
